import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  FileText,
  Receipt,
  ClipboardText,
  Download,
  User,
  Phone,
  Envelope,
  MapPin,
  SignOut,
  CalendarCheck,
  CurrencyDollar,
  Clock,
  CheckCircle
} from '@phosphor-icons/react';


function StatusBadge({ status }) {
  const styles = {
    'Draft': 'bg-slate-100 text-slate-700',
    'Sent': 'bg-blue-100 text-blue-700',
    'Approved': 'bg-emerald-100 text-emerald-700',
    'Signed': 'bg-indigo-100 text-indigo-700',
    'Paid': 'bg-green-100 text-green-700',
    'Pending': 'bg-amber-100 text-amber-700',
    'Pending Approval': 'bg-amber-100 text-amber-700',
    'scheduled': 'bg-blue-100 text-blue-700',
    'in_progress': 'bg-purple-100 text-purple-700',
    'completed': 'bg-green-100 text-green-700',
    'Completed': 'bg-green-100 text-green-700',
    'Cancelled': 'bg-red-100 text-red-700',
    'Overdue': 'bg-red-100 text-red-700',
    'confirmed': 'bg-green-100 text-green-700',
    'pending': 'bg-amber-100 text-amber-700',
  };
  const label = {
    'scheduled': 'Programada', 'completed': 'Completada', 'in_progress': 'En Progreso',
    'confirmed': 'Confirmada', 'pending': 'Pendiente'
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {label[status] || status}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4" data-testid={testId}>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center shrink-0`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function ClientPortalPage() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortalData();
  }, []);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      const [quotesRes, invoicesRes, workOrdersRes, bookingsRes, infoRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/my-quotes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/my-invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/my-work-orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/my-bookings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/my-info`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
      setWorkOrders(workOrdersRes.data);
      setBookings(bookingsRes.data);
      setClientInfo(infoRes.data);
    } catch (error) {
      console.error('Error fetching portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (type, id) => {
    try {
      const endpoint = type === 'quote' ? `quotes/${id}/pdf` : `invoices/${id}/pdf`;
      const response = await axios.get(`${API_URL}/api/${endpoint}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const tabs = [
    { id: 'quotes', label: 'Cotizaciones', icon: FileText, count: quotes.length },
    { id: 'invoices', label: 'Facturas', icon: Receipt, count: invoices.length },
    { id: 'work-orders', label: 'Ordenes de Trabajo', icon: ClipboardText, count: workOrders.length },
    { id: 'bookings', label: 'Citas', icon: CalendarCheck, count: bookings.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  // Summary stats
  const totalQuotesValue = quotes.reduce((sum, q) => sum + (q.total || 0), 0);
  const pendingInvoices = invoices.filter(i => i.status !== 'Paid').length;
  const activeOrders = workOrders.filter(w => w.status === 'scheduled' || w.status === 'in_progress').length;
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_0246dde3-e1c1-4ddd-b1f0-cbdadc084ea8/artifacts/indb97if_WhatsApp%20Image%202026-02-08%20at%208.04.43%20PM%20%281%29.jpeg"
              alt="IQES Logo" className="w-10 h-10 rounded-lg object-contain"
            />
            <div>
              <h1 className="text-lg font-bold">IQES Low Voltage Solutions</h1>
              <p className="text-slate-400 text-xs">Portal de Cliente</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
            <button onClick={logout} className="p-2 hover:bg-slate-800 rounded-lg transition-colors" data-testid="portal-logout">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        {/* Client Info Card */}
        {clientInfo && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <User size={24} className="text-blue-600" />
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <p className="font-bold text-lg text-slate-900">{clientInfo.name}</p>
                  {clientInfo.company && <p className="text-sm text-slate-500">{clientInfo.company}</p>}
                </div>
                <div className="space-y-1">
                  {clientInfo.phone && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone size={14} className="text-slate-400" /> {clientInfo.phone}
                    </p>
                  )}
                  {clientInfo.email && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <Envelope size={14} className="text-slate-400" /> {clientInfo.email}
                    </p>
                  )}
                </div>
                <div>
                  {clientInfo.address && (
                    <p className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin size={14} className="text-slate-400" />
                      {clientInfo.address}, {clientInfo.city} {clientInfo.state} {clientInfo.zip_code}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-summary">
          <SummaryCard icon={FileText} label="Total Cotizaciones" value={`$${totalQuotesValue.toLocaleString()}`} color="bg-blue-500" testId="summary-quotes" />
          <SummaryCard icon={Receipt} label="Facturas Pendientes" value={pendingInvoices} color="bg-amber-500" testId="summary-invoices" />
          <SummaryCard icon={ClipboardText} label="Ordenes Activas" value={activeOrders} color="bg-purple-500" testId="summary-orders" />
          <SummaryCard icon={CalendarCheck} label="Citas Programadas" value={upcomingBookings} color="bg-emerald-500" testId="summary-bookings" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-slate-200 pb-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 font-medium text-sm whitespace-nowrap transition-colors border-b-2 -mb-[1px] ${
                  activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
                data-testid={`portal-tab-${tab.id}`}
              >
                <Icon size={16} />
                {tab.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'
                }`}>{tab.count}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {activeTab === 'quotes' && (
            <div>
              {quotes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="portal-quotes-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Numero</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Fecha</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Total</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Estado</th>
                        <th className="px-5 py-3 text-right text-xs font-bold uppercase text-slate-500">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {quotes.map((q) => (
                        <tr key={q.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3.5 font-medium text-sm">{q.quote_number}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{new Date(q.created_at).toLocaleDateString('es-ES')}</td>
                          <td className="px-5 py-3.5 font-semibold text-sm">${q.total?.toLocaleString()}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={q.status} /></td>
                          <td className="px-5 py-3.5 text-right">
                            <button onClick={() => handleDownloadPdf('quote', q.id)}
                              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500" data-testid={`portal-download-quote-${q.id}`}>
                              <Download size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={FileText} message="No hay cotizaciones disponibles" />
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div>
              {invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="portal-invoices-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Numero</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Fecha</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Vencimiento</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Total</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Estado</th>
                        <th className="px-5 py-3 text-right text-xs font-bold uppercase text-slate-500">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3.5 font-medium text-sm">{inv.invoice_number}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{new Date(inv.created_at).toLocaleDateString('es-ES')}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{inv.due_date ? new Date(inv.due_date).toLocaleDateString('es-ES') : '-'}</td>
                          <td className="px-5 py-3.5 font-semibold text-sm">${inv.total?.toLocaleString()}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={inv.status} /></td>
                          <td className="px-5 py-3.5 text-right">
                            <button onClick={() => handleDownloadPdf('invoice', inv.id)}
                              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500" data-testid={`portal-download-invoice-${inv.id}`}>
                              <Download size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={Receipt} message="No hay facturas disponibles" />
              )}
            </div>
          )}

          {activeTab === 'work-orders' && (
            <div>
              {workOrders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="portal-work-orders-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Orden</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Titulo</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Fecha Programada</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {workOrders.map((wo) => (
                        <tr key={wo.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3.5 font-medium text-sm">{wo.work_order_number || wo.id?.slice(0,8)}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-700">{wo.title || '-'}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">
                            {wo.scheduled_date ? new Date(wo.scheduled_date).toLocaleDateString('es-ES') : 'Por definir'}
                          </td>
                          <td className="px-5 py-3.5"><StatusBadge status={wo.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={ClipboardText} message="No hay ordenes de trabajo disponibles" />
              )}
            </div>
          )}

          {activeTab === 'bookings' && (
            <div>
              {bookings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="portal-bookings-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Fecha</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Hora</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Servicio</th>
                        <th className="px-5 py-3 text-left text-xs font-bold uppercase text-slate-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {bookings.map((b) => (
                        <tr key={b.id} className="hover:bg-slate-50">
                          <td className="px-5 py-3.5 font-medium text-sm">{b.date}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{b.time || '-'}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-700">{b.service || b.notes || '-'}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={b.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={CalendarCheck} message="No hay citas programadas" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between text-xs text-slate-400">
          <p>IQES LLC &copy; 2026</p>
          <p>Tel: 786-436-7307 | info@iqeslowvoltage.com</p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="py-16 text-center text-slate-400">
      <Icon size={40} className="mx-auto mb-3 text-slate-300" />
      <p>{message}</p>
    </div>
  );
}
